import React from 'react';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

/**
 * PageZoomControls — floating pill in the top-right of a page.
 *
 * Renders `−`, current % label, `+`, and a reset button. Meant to be
 * paired with usePageZoom() where the page wrapper applies the returned
 * `scale` via CSS transform.
 *
 * Positioning: absolute inside the wrapper's transformed content would
 * itself shrink/grow with zoom. Consumers MUST render this OUTSIDE the
 * transformed wrapper (e.g. as a sibling of the wrapper) or use
 * `position: fixed` so the toolbar stays a consistent size.
 */
export default function PageZoomControls({
  scale,
  onZoomIn,
  onZoomOut,
  onReset,
  minScale = 0.5,
  maxScale = 2.0,
  className = '',
  label = 'Zoom',
  testIdPrefix = 'page-zoom',
}) {
  const pct = Math.round(scale * 100);
  return (
    <div
      className={`inline-flex items-center gap-0.5 bg-white border border-slate-200 rounded-full shadow-sm px-1 py-0.5 text-xs ${className}`}
      title={`${label} — persists for this page`}
      data-testid={`${testIdPrefix}-controls`}
    >
      <button
        type="button"
        onClick={onZoomOut}
        disabled={scale <= minScale}
        className="h-6 w-6 grid place-items-center rounded-full text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
        title={`Zoom out (${label.toLowerCase()})`}
        data-testid={`${testIdPrefix}-out`}
      >
        <ZoomOut className="h-3.5 w-3.5" />
      </button>
      <span
        className="min-w-[3ch] text-center font-medium text-slate-700 tabular-nums select-none"
        data-testid={`${testIdPrefix}-value`}
      >
        {pct}%
      </span>
      <button
        type="button"
        onClick={onZoomIn}
        disabled={scale >= maxScale}
        className="h-6 w-6 grid place-items-center rounded-full text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
        title={`Zoom in (${label.toLowerCase()})`}
        data-testid={`${testIdPrefix}-in`}
      >
        <ZoomIn className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={onReset}
        disabled={scale === 1}
        className="h-6 w-6 grid place-items-center rounded-full text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
        title="Reset to 100%"
        data-testid={`${testIdPrefix}-reset`}
      >
        <RotateCcw className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
